import BasicButton from "@components/core/buttons/BasicButton";
import PageLayout from "@components/core/layouts/PageLayout";
import BasicLink from "@components/core/links/BasicLink";
import ConfirmModal from "@components/core/modals/ConfirmModal";
import GuestModal from "@components/core/modals/GuestModal";
import InfoModal from "@components/core/modals/InfoModal";
import CardSection from "@components/core/section/CardSection";
import BasicTable from "@components/core/tables/BasicTable";
import BasicTabs from "@components/core/tabs/BasicTabs";
import { ROUTES } from "@routes/Routes";
import {
  requestAddGuestForOffices,
  requestDeleteGuestForOffices,
  requestPostMembersForOffice,
  requestSearchGuestsForOffice,
  requestUpdateAllGuestPicturePriority,
  requestUpdateGuestForOffice,
  requestUpdateGuestPicturePriority,
} from "@state/users/UserEffects";
import { Contact } from "@type/core/dashboard/dashboard.types";
import { Guest } from "@type/core/offices/offices.types";
import { GUESTS_DELEGATION_SELECTION } from "@utils/Constant";
import { toastError } from "@utils/helpers/toast-helper";
import useOfficesToDisplay from "@utils/hooks/useOfficesToDisplay";
import { columns as guestsColumns } from "@views/core/extranet/menu/offices/users/columns/GuestsColumns";
import { columns as membersColumns } from "@views/core/extranet/menu/offices/users/columns/MembersColumns";
import { TablePaginationConfig } from "antd";
import Search from "antd/es/input/Search";
import { FilterValue, SorterResult } from "antd/es/table/interface";
import { FunctionComponent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import configuration from "@utils/Config";

const UsersScreen: FunctionComponent = () => {
  const { t } = useTranslation();
  const [isLoading, setLoading] = useState<boolean>(false);

  const { officesToDisplay, handleTabClick, activeKey, selectedOffice } =
    useOfficesToDisplay();
  const offices = useMemo(
    () =>
      officesToDisplay.filter((office) =>
        office.rights?.includes("GESTION_CABINETS"),
      ),
    [officesToDisplay],
  );
  const officeIds = useMemo(
    () =>
      officesToDisplay
        .filter((office) => office.rights?.includes("GESTION_CABINETS"))
        .map((office) => office.id),
    [officesToDisplay],
  );

  const [isDeleteGuestOpen, setIsDeleteGuestOpen] = useState<boolean>(false);
  const [isUpdateGuestOpen, setIsUpdateGuestOpen] = useState<boolean>(false);
  const [isAddGuestOpen, setIsAddGuestOpen] = useState<boolean>(false);
  const [isSignaturePicturePriorityOpen, setIsSignaturePicturePriorityOpen] =
    useState<boolean>(false);

  const [search, setSearch] = useState<string>("");
  const [sorter, setSorter] = useState<{ field: string; order: number }>({
    field: "email",
    order: 0,
  });
  const [guests, setGuests] = useState<Guest[]>([]);
  const [members, setMembers] = useState<Contact[]>([]);

  const [selectedGuest, setSelectedGuest] = useState<Guest>();
  const [officeId, setOfficeId] = useState<string>("");
  const [pendingSignaturePriorityChange, setPendingSignaturePriorityChange] =
    useState<{ newPriority: string; guestId?: string }>({
      newPriority: "DEFAULT_SIGNATURE_PICTURE",
      guestId: undefined,
    });

  useEffect(() => {
    if (officeIds) {
      setLoading(true);
      void requestPostMembersForOffice({ dto: officeIds });
    }
  }, [officeIds]);

  useEffect(() => {
    return requestPostMembersForOffice.done.watch(({ result }) => {
      if (result.ok && result.data) {
        setMembers(result.data.contacts);
      } else {
        toastError(`${t("error.toast.officesSearch")} ${officeId}`);
      }
      setLoading(false);
    });
  });

  useEffect(() => {
    handleSearch(selectedOffice?.id);
  }, []);

  const retrieveOfficeMembers = (officeId: string, members?: Contact[]) => {
    const validMembers: Contact[] = [];
    members?.map((member) => {
      if (member.officeId === officeId) {
        validMembers?.push(member);
      }
    });
    return validMembers;
  };

  const handleSearch = (officeId?: string) => {
    if (!officeId) {
      return;
    }
    setOfficeId(officeId);
    void requestSearchGuestsForOffice({
      dto: {
        page: 1,
        search: search,
        profile: null,
        sort: {
          order: sorter.order,
          type: sorter.field,
        },
        officeid: officeId,
      },
    }).then((result) => {
      if (result.ok && result.data) {
        setGuests(result.data.content);
      } else {
        toastError(t("offices.users.guests.search.error"));
      }
    });
  };

  const handleTableChange = (
    _pagination: TablePaginationConfig,
    _filters: Record<string, FilterValue | null>,
    sorter: SorterResult<Guest> | SorterResult<Guest>[],
  ) => {
    const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter;
    setSorter({
      field: singleSorter.field as string,
      order: singleSorter.order === "ascend" ? 1 : 0,
    });
    handleSearch(officeId);
  };

  const handleUpdateGuest = (values: {
    email: string;
    delegations: string[];
  }) => {
    void requestUpdateGuestForOffice({
      dto: {
        delegations: values.delegations,
        email: values.email,
        id: selectedGuest?.id,
        officeId: officeId,
        profile: selectedGuest?.profile,
      },
      id: selectedGuest?.id,
    }).then((result) => {
      if (result.ok) {
        handleSearch(officeId);
      } else {
        toastError(t("offices.users.guests.modifyGuest.error"));
      }
    });
  };

  const handleAddGuest = (values: { email: string; delegations: string[] }) => {
    void requestAddGuestForOffices({
      dto: {
        delegations: values.delegations,
        email: values.email,
        id: "",
        officeId: officeId,
        profile: "",
      },
    }).then((result) => {
      if (result.ok) {
        handleSearch(officeId);
      } else {
        toastError(t("offices.users.guests.addGuest.error"));
      }
    });
  };

  const handleDeleteGuest = () => {
    void requestDeleteGuestForOffices({
      dto: undefined,
      id: selectedGuest?.id,
    }).then((result) => {
      if (result.ok) {
        handleSearch(officeId);
      } else {
        toastError(t("offices.users.guests.deleteGuest.error"));
      }
    });
  };

  const handleSignaturePriorityChange = (
    newPriority: string,
    guestId: string | undefined,
    allGuests: boolean,
  ) => {
    if (allGuests) {
      void requestUpdateAllGuestPicturePriority({
        dto: { signaturePriority: newPriority },
        id: officeId,
        value: newPriority,
      }).then((result) => {
        if (result.ok) {
          handleSearch(officeId);
        } else {
          toastError(
            t("offices.users.guests.signaturePicturePriority.allError"),
          );
        }
      });
    } else {
      void requestUpdateGuestPicturePriority({
        dto: { signaturePriority: newPriority },
        id: selectedGuest?.id,
        value: newPriority,
      }).then((result) => {
        if (result.ok) {
          handleSearch(officeId);
        } else {
          toastError(t("offices.users.guests.signaturePicturePriority.error"));
        }
      });
    }
  };

  return (
    <PageLayout container returnRoute={ROUTES.extranet.home.generate()}>
      <>
        <CardSection
          title={
            <>
              {t("offices.users.title")}
              <InfoModal value="administrationCROEC" info />
            </>
          }
        >
          <BasicTabs
            activeKey={activeKey}
            onTabClick={handleTabClick}
            onTabChange={handleSearch}
            tabs={offices.map((office) => {
              return {
                title: office.alias,
                content: (
                  <>
                    {office.rights &&
                      office.rights.includes(
                        "ADMINISTRATION_CROEC_AFFICHAGE",
                      ) && (
                        <>
                          <div className="mb-24">
                            <h3>
                              {t("offices.users.guests.title")}
                              <InfoModal value="guest" info />
                            </h3>
                            <div className="d-flex align-items-center justify-content-center mb-24">
                              <BasicButton
                                text={t("offices.users.guests.addGuest.title")}
                                className="mw-340"
                                onClick={() => setIsAddGuestOpen(true)}
                              />
                            </div>
                            <div className="d-flex align-items-center justify-content-center mb-16">
                              <Search
                                id="filterSearch"
                                placeholder={t(
                                  "offices.users.guests.researchGuest",
                                )}
                                value={search}
                                onChange={(e) => {
                                  e.preventDefault();
                                  setSearch(e.target.value);
                                  handleSearch(office.id);
                                }}
                                className="search w-md-50"
                              />
                            </div>
                          </div>
                          <div className="min-height-table">
                            <BasicTable<Guest>
                              isLoading={isLoading}
                              columns={guestsColumns(
                                setIsDeleteGuestOpen,
                                setIsUpdateGuestOpen,
                                setIsSignaturePicturePriorityOpen,
                                setSelectedGuest,
                                setPendingSignaturePriorityChange,
                              )}
                              onChange={handleTableChange}
                              dataSource={guests}
                            />
                          </div>
                        </>
                      )}
                    {office.rights &&
                      !office.rights.includes(
                        "ADMINISTRATION_CROEC_AFFICHAGE",
                      ) && (
                        <>
                          <div className="mb-16">
                            <h3>
                              {t("offices.users.title")}
                              <InfoModal value="user" info />
                            </h3>
                          </div>
                          <div className="mb-8">
                            {t("account.footer")}
                            <BasicLink
                              to={
                                configuration.comptExpertUrl +
                                "moncompte/mes-infos"
                              }
                              className="text-primary"
                            >
                              {t("account.comptExpert")}
                            </BasicLink>
                            {"."}
                          </div>
                          <div className="mb-8">
                            {t("offices.users.extraInformation")}
                          </div>
                          <div className="min-height-table">
                            <BasicTable
                              isLoading={isLoading}
                              columns={membersColumns()}
                              dataSource={retrieveOfficeMembers(
                                office.id,
                                members,
                              )}
                            />
                          </div>
                        </>
                      )}
                  </>
                ),
                officeId: office.id,
              };
            })}
          />
        </CardSection>
        <ConfirmModal
          visible={isDeleteGuestOpen}
          onCancel={() => setIsDeleteGuestOpen(false)}
          onOk={() => {
            handleDeleteGuest();
            setIsDeleteGuestOpen(false);
          }}
          title={t("offices.users.guests.deleteGuest.title")}
        />
        <GuestModal
          visible={isAddGuestOpen}
          onCancel={() => setIsAddGuestOpen(false)}
          delegations={GUESTS_DELEGATION_SELECTION}
          guest={undefined}
          onOk={(values) => {
            handleAddGuest(values);
            setIsAddGuestOpen(false);
          }}
          title={t("offices.users.guests.addGuest.title")}
        />
        <GuestModal
          visible={isUpdateGuestOpen}
          onCancel={() => setIsUpdateGuestOpen(false)}
          delegations={GUESTS_DELEGATION_SELECTION}
          guest={selectedGuest}
          onOk={(values) => {
            handleUpdateGuest(values);
            setIsUpdateGuestOpen(false);
          }}
          title={t("offices.users.guests.modifyGuest.title")}
        />
        <ConfirmModal
          visible={isSignaturePicturePriorityOpen}
          onCancel={() => {
            handleSignaturePriorityChange(
              pendingSignaturePriorityChange.newPriority,
              pendingSignaturePriorityChange.guestId,
              false,
            );
            setIsSignaturePicturePriorityOpen(false);
          }}
          onOk={() => {
            handleSignaturePriorityChange(
              pendingSignaturePriorityChange.newPriority,
              pendingSignaturePriorityChange.guestId,
              true,
            );
            setIsSignaturePicturePriorityOpen(false);
          }}
          title={t("offices.users.guests.signaturePicturePriority.title")}
        />
      </>
    </PageLayout>
  );
};

export default UsersScreen;
